/**
 * Presentation rules for access codes (f15.4), kept pure so they are testable
 * without rendering. The status itself is always the server's effective status
 * (BR-F15.4-06) -- nothing here recomputes it.
 */

import type { AccessPassword, AccessPasswordStatusCode } from '@/api/client';

const PILLS: Record<AccessPasswordStatusCode, string> = {
  ACTIVE: 'pill-success',
  EXPIRED: 'pill-neutral',
  EXHAUSTED: 'pill-warning',
  REVOKED: 'pill-danger',
};

/** Active green, Expired neutral, Exhausted amber, Revoked red (the prototype's pills). */
export function statusPill(code: string): string {
  return PILLS[code as AccessPasswordStatusCode] ?? 'pill-neutral';
}

export function isActive(password: Pick<AccessPassword, 'status'>): boolean {
  return password.status.code === 'ACTIVE';
}

/** `count / max`, or `count / ∞` when unlimited. */
export function redemptionsText(password: Pick<AccessPassword, 'redemption_count' | 'max_redemptions'>): string {
  const max = password.max_redemptions;
  return `${password.redemption_count} / ${max === null || max === undefined ? '∞' : max}`;
}

/** The Expires field's default: today + 90 days, as the date input's `YYYY-MM-DD`. */
export function defaultExpiry(today: Date = new Date()): string {
  const day = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate() + 90));
  return day.toISOString().slice(0, 10);
}

/** Why a Revoke button is off, or what a revoked code was revoked for. */
export function revokeTooltip(password: AccessPassword): string {
  if (password.status.code === 'REVOKED') {
    return password.revocation_reason ? `Revoked: ${password.revocation_reason}` : 'Revoked';
  }
  return isActive(password) ? 'Revoke' : `${password.status.name} — issue a new code instead`;
}
